import classNames from "classnames/bind";
import React, { useState } from "react";
import { Button } from "../../../Button";
import { Icon } from "../../../Icon";
import { ImageLightbox } from "../../../ImageLightbox";
import { Popover } from "../../../Popover";
import { FileRenderer } from "../FileRenderer";
import type { FilePreviewPopoverProps } from "../types";
import styles from "./FilePreviewPopover.module.scss";

const cx = classNames.bind(styles);

const FilePreviewPopover = ({
  fileName,
  fileType,
  fileMimeType,
  fileExtension,
  fileObjectUrl,
  onDownload,
  rootProps,
  errorMessage,
  children,
  isLoading,
  isBusy,
  isValid,
  ...restPopoverProps
}: FilePreviewPopoverProps) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const isImage = fileType === "IMAGE";
  const canZoom =
    isImage && !errorMessage && !isLoading && !isBusy && fileObjectUrl;

  return (
    <>
      <Popover
        {...restPopoverProps}
        rootProps={rootProps}
        contentRender={() => (
          <div className={cx("preview-container")}>
            <div className={cx("preview-details")}>
              <p className={cx("file-name", !!errorMessage && "has-error")}>
                {errorMessage ? (
                  <Icon
                    variant="warning"
                    size={16}
                    className={cx("icon-error")}
                  />
                ) : null}
                {fileName}
              </p>
              {fileName ? (
                <Button
                  variant="outline"
                  icon="download-simple"
                  disabled={Boolean(errorMessage) || isLoading || isBusy}
                  isLoading={isLoading}
                  className={cx("button")}
                  title={`Download link for ${fileName}`}
                  aria-label={`Click to download ${fileName} to your device.`}
                  onClick={onDownload}
                />
              ) : null}
            </div>
            <div
              className={cx("preview-viewport", { "is-zoomable": canZoom })}
              onClick={canZoom ? () => setIsLightboxOpen(true) : undefined}
              role={canZoom ? "button" : undefined}
              tabIndex={canZoom ? 0 : undefined}
              onKeyDown={
                canZoom
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setIsLightboxOpen(true);
                      }
                    }
                  : undefined
              }
              aria-label={
                canZoom ? `Click to view ${fileName} in fullscreen` : undefined
              }
            >
              <FileRenderer
                fileType={fileType}
                fileExtension={fileExtension}
                fileMimeType={fileMimeType}
                fileObjectUrl={fileObjectUrl ?? ""}
                isLoading={isLoading}
                isBusy={isBusy}
                isValid={isValid}
                errorMessage={errorMessage}
              />
            </div>
          </div>
        )}
        contentClassName={cx("content-container")}
        shouldDisplayArrow={false}
      >
        {children}
      </Popover>

      {canZoom ? (
        <ImageLightbox
          isOpen={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
          imageSrc={fileObjectUrl}
          imageAlt={fileName ?? undefined}
        />
      ) : null}
    </>
  );
};

export default FilePreviewPopover;
